import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, User, Mail, Shield, Key, Loader2 } from 'lucide-react';

export const ProfilePage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (newPassword.length < 4) {
            toast.error('Passwort muss mindestens 4 Zeichen haben');
            return;
        }
        
        if (newPassword !== confirmPassword) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        
        setSaving(true);
        try {
            await changePassword(newPassword);
            toast.success('Passwort erfolgreich geändert');
            setPasswordDialogOpen(false);
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-4 pb-24" data-testid="profile-page">
            <h1 className="text-xl md:text-2xl font-bold">Profil</h1>

            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Benutzerinformationen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold">
                            {user?.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                            <p className="text-xl font-semibold">{user?.name}</p>
                            <p className="text-muted-foreground">{user?.role === 'ADMIN' ? 'Administrator' : 'Mitarbeiter'}</p>
                        </div>
                    </div>

                    <div className="pt-4 space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                            <Mail className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm text-muted-foreground">E-Mail</p>
                                <p className="font-medium">{user?.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                            <Shield className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm text-muted-foreground">Rolle</p>
                                <p className="font-medium">{user?.role === 'ADMIN' ? 'Administrator' : 'Mitarbeiter'}</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Sicherheit</CardTitle>
                </CardHeader>
                <CardContent>
                    <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => setPasswordDialogOpen(true)}
                        data-testid="change-password-btn"
                    >
                        <Key className="h-4 w-4 mr-2" />
                        Passwort ändern
                    </Button>
                </CardContent>
            </Card>

            <Button 
                variant="destructive" 
                className="w-full" 
                onClick={handleLogout}
                data-testid="logout-btn"
            >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
            </Button>

            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Passwort ändern</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Neues Passwort</Label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Mindestens 4 Zeichen"
                                required
                                data-testid="new-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Passwort bestätigen</Label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Passwort wiederholen"
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProfilePage;
